'use client'
import * as React from 'react'
import { SafeImage } from '@/components/marketing/safe-image'
import Link from 'next/link'
import { Heart, MessageCircle, Sparkles, ArrowRight, ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { type DemoPost, type DemoProduct, getDemoProduct } from '@/lib/copy/demo-products'
import { trackGalleryView, trackGalleryInteraction } from '@/lib/analytics/ga4'
import { AddToCartModal } from './add-to-cart-modal'
import { InteractiveShoppableHero, type HeroSlide } from './interactive-shoppable-hero'

// Self-contained, fully-styled widget preview that mimics the real
// app.idukki.io output. Uses curated Unsplash lifestyle photos for a
// believable "user post" feel, overlays visible product hotspots that
// open AddToCartModal on click. Drop on any marketing page.
export function WidgetDemoPreview({
  posts,
  layout = 'grid',
  cta = { label: 'Build this on your store free', href: 'https://app.idukki.io/register' },
  secondary = { label: 'Open the playground', href: 'https://app.idukki.io/preview' },
  title = 'Live preview. Idukki shoppable UGC',
  subtitle = 'Tap any pin to open the product card.',
  showJourney = false,
  journeyLayout = 'stack',
  hideGrid = false,
  products,
  analyticsSurface = 'widget_preview',
}: {
  posts: DemoPost[]
  layout?: 'grid' | 'carousel'
  cta?: { label: string; href: string }
  secondary?: { label: string; href: string }
  title?: string
  subtitle?: string
  /** Optional per-call product registry (e.g. vertical products derived from
   *  the gallery JSONs). Resolved before the global demoProducts table so a
   *  page can supply its own catalogue without registering it globally. */
  products?: Record<string, DemoProduct>
  /** GA4 surface label for gallery_view / gallery_interaction events. */
  analyticsSurface?: string
  /** Renders the animated MobileShoppableJourney above the grid using
   *  the first post's video/image + first hotspot's product. Use this
   *  on top-of-funnel pages (industry hero, widget hero) where the
   *  end-to-end motion sells better than a static grid alone. */
  showJourney?: boolean
  /** When 'side', renders the journey hero and grid card side-by-side on lg screens.
   *  Defaults to 'stack' (original stacked behaviour). */
  journeyLayout?: 'stack' | 'side'
  /** When true (requires showJourney), renders only the phone mockup and
   *  omits the grid card + CTA panel entirely. */
  hideGrid?: boolean
}) {
  const [active, setActive] = React.useState<{ productId: string; post: DemoPost } | null>(null)
  const resolveProduct = React.useCallback(
    (id: string): DemoProduct | null => products?.[id] ?? getDemoProduct(id),
    [products],
  )
  const product = active ? resolveProduct(active.productId) : null

  // Fire a single gallery_view impression when the grid scrolls into view.
  const gridRef = React.useRef<HTMLUListElement>(null)
  const impressionSent = React.useRef(false)
  React.useEffect(() => {
    const el = gridRef.current
    if (!el || impressionSent.current) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !impressionSent.current) {
            impressionSent.current = true
            trackGalleryView({ surface: analyticsSurface, gallery: title, layout })
            io.disconnect()
          }
        }
      },
      { threshold: 0.4 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [analyticsSurface, title, layout])

  // Build interactive shoppable slides from the posts so this frame is a live
  // mini-widget (tap a pin to shop, swipe for the next video) everywhere this
  // preview renders — the same component as the homepage hero.
  const journeySlides: HeroSlide[] = posts
    .map((post) => ({
      image: post.image,
      videoSrc: post.videoSrc,
      user: post.user,
      quote: post.caption,
      products: post.hotspots
        .map((h) => {
          const p = resolveProduct(h.productId)
          return p
            ? {
                x: h.x,
                y: h.y,
                name: p.name,
                price: p.price,
                msrp: p.compareAt,
                badge: p.discountLabel,
                image: p.image,
              }
            : null
        })
        .filter((x): x is NonNullable<typeof x> => x !== null),
    }))
    .filter((s) => s.products.length > 0)

  const hasJourney = showJourney && journeySlides.length > 0
  const isSide = hasJourney && journeyLayout === 'side'

  if (hideGrid && hasJourney) {
    return (
      <>
        <div className="mx-auto max-w-[360px]">
          <InteractiveShoppableHero slides={journeySlides} />
        </div>
        <AddToCartModal product={product} post={active?.post} onClose={() => setActive(null)} />
      </>
    )
  }

  return (
    <>
      {/* Scoped preconnect: this preview posters raw unsplash URLs directly
          on a <video>, bypassing next/image's server-side proxy, so this is
          one of the few surfaces that actually benefits from it. */}
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="anonymous" />
      <div className={isSide ? 'grid gap-6 lg:grid-cols-2 lg:items-start' : undefined}>
        {hasJourney && !isSide && (
          <div className="mb-4">
            <InteractiveShoppableHero slides={journeySlides} />
          </div>
        )}
        {isSide && <InteractiveShoppableHero slides={journeySlides} />}

        <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
          <div className="flex flex-col gap-2 border-b p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary">
                Live preview
              </p>
              <h3 className="mt-1 font-display text-base font-medium tracking-tight sm:text-lg">
                {title}
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">{subtitle}</p>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:animate-none" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              Live · 37 KB runtime
            </div>
          </div>

          <ul
            ref={gridRef}
            className={cn(
              'grid gap-1.5 bg-secondary/30 p-3 sm:p-4',
              layout === 'grid' && 'grid-cols-3',
              layout === 'carousel' && 'grid-cols-2 sm:grid-cols-4',
            )}
          >
            {posts.map((p, i) => (
              <PreviewTile
                key={i}
                post={p}
                resolve={resolveProduct}
                onHotspot={(productId) => {
                  trackGalleryInteraction({
                    action: 'pin_click',
                    surface: analyticsSurface,
                    gallery: title,
                    product: resolveProduct(productId)?.name,
                  })
                  setActive({ productId, post: p })
                }}
              />
            ))}
          </ul>

          <div className="flex flex-col gap-2 border-t bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <p className="text-xs text-muted-foreground sm:text-sm">
              <Sparkles className="mr-1 inline h-3 w-3 text-primary" />
              Click any pin to open the product card · Plug your handle, paste the embed.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button asChild size="sm">
                <Link
                  href={cta.href}
                  target={cta.href.startsWith('http') ? '_blank' : undefined}
                  rel={cta.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                  {cta.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
              {secondary && (
                <Button asChild size="sm" variant="outline">
                  <Link
                    href={secondary.href}
                    target={secondary.href.startsWith('http') ? '_blank' : undefined}
                    rel={secondary.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {secondary.label}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddToCartModal product={product} post={active?.post} onClose={() => setActive(null)} />
    </>
  )
}

// One tile of the preview grid. Pulled out so we can:
// - render an autoplay/muted/loop <video> when the post has a videoSrc
//   (Tolstoy-style shoppable preview)
// - fall back to the static image on either video error or photo error
// - keep hotspots + user/like overlay identical across both modes
function PreviewTile({
  post,
  onHotspot,
  resolve,
}: {
  post: DemoPost
  onHotspot: (id: string) => void
  resolve: (id: string) => DemoProduct | null
}) {
  // useVideo flips back to false on <video> error so the photo takes over.
  const [useVideo, setUseVideo] = React.useState<boolean>(Boolean(post.videoSrc))

  return (
    <li className="group relative aspect-square overflow-hidden rounded-md bg-muted">
      {useVideo && post.videoSrc ? (
        <video
          src={post.videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={post.image}
          onError={() => setUseVideo(false)}
          className="absolute inset-0 h-full w-full object-cover transition group-hover:scale-105"
        />
      ) : (
        <SafeImage
          src={post.image}
          alt={post.caption}
          fill
          sizes="(max-width: 768px) 33vw, 200px"
          className="object-cover transition group-hover:scale-105"
        />
      )}

      {/* "PLAYING" pip, only when the tile is actually playing video */}
      {useVideo && post.videoSrc && (
        <span className="absolute left-1.5 top-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-foreground/85 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-background backdrop-blur">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-1.5 w-1.5 animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:animate-none" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          Live
        </span>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent p-2 text-[10px] text-white">
        <span className="truncate">{post.user}</span>
        <span className="flex items-center gap-1.5">
          <span className="flex items-center gap-1">
            <Heart className="h-3 w-3" /> {post.likes}
          </span>
          <span className="flex items-center gap-1">
            <MessageCircle className="h-3 w-3" /> {post.comments}
          </span>
        </span>
      </div>

      {post.hotspots.map((h, k) => (
        <button
          key={k}
          type="button"
          aria-label={`Shop ${resolve(h.productId)?.name || 'tagged product'}`}
          onClick={() => onHotspot(h.productId)}
          className="absolute z-10 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-lg ring-2 ring-white/60 transition hover:scale-110 focus:outline-none focus:ring-4 focus:ring-primary/40"
          style={{ left: `${h.x}%`, top: `${h.y}%` }}
        >
          <ShoppingBag className="h-3 w-3 text-primary" />
          <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-white/80 motion-reduce:animate-none" />
        </button>
      ))}
    </li>
  )
}
